/**
 * Workspace-status names reserved for the Super Agent AI lane. These are seeded per
 * workspace on onboarding and are matched by name (the board classifier buckets
 * project-scoped statuses purely by these names), so a user must not be able to create
 * or rename a custom status onto one of them.
 *
 * This mirrors the backend guard — keep in sync with:
 *   - moly-platform/src/Moly/Domain/Constants/AiStatusNames.cs (authoritative)
 *   - moly_superagent/bridge/src/aiStatus.ts (AI_STATUS_TEMPLATE labels)
 *
 * Client-side this is UX only; WorkspaceStatusesSyncController rejects reserved names on
 * push regardless.
 */
export const RESERVED_AI_STATUS_NAMES = [
  'AI Ready',
  'AI Planning',
  'AI Plan Review',
  'AI In Progress',
  'AI Pending Test',
  'AI In Review',
  'AI Needs Attention',
  'AI Ready to Merge',
  'AI Completed',
] as const

const RESERVED_LOOKUP = new Set(
  RESERVED_AI_STATUS_NAMES.map((name) => name.toLowerCase()),
)

/**
 * True if `name` collides with a reserved AI-lane name, ignoring case, surrounding
 * whitespace, and internal whitespace runs (so `ai  ready` and ` AI Ready ` both match).
 */
export function isReservedStatusName(name: string): boolean {
  const normalized = name.trim().replace(/\s+/g, ' ').toLowerCase()
  if (normalized.length === 0) return false
  return RESERVED_LOOKUP.has(normalized)
}
